/**
 * Шаги «Визуалы» и «Карточки» пряников — хуки: выбор кабинетов, мета артикулов, план и смета, опрос запусков.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sellerTargetKey, useSellerTargets, type SellerTarget, type SellerTargetsController } from '../../../components/useSellerTargets'
import type { RunSummary, SeriesEstimate } from '../../content-funnel-series/types'
import { gingerbreadVisualsFlowAPI } from './api'
import {
  buildVisualsPlan,
  chunk,
  DEFAULT_VISUALS_PROFILE,
  distinctSetGroups,
  estimateTotal,
  hasPendingRuns,
  matchKeyOf,
  MAX_ARTICLES_PER_CALL,
  pickRuns,
  RUNS_POLL_MS,
  skuTargetOf,
  type ArticlePlan,
  type EstimateTotal,
} from './model'
import type { ArticleMeta, SlideSetMatch, VisualsProfile } from './types'

const errorText = (error: unknown, fallback: string): string => {
  const value = error as { response?: { data?: { error?: { message?: string } } }; message?: string } | null
  return value?.response?.data?.error?.message || value?.message || fallback
}

// ── Выбор кабинетов ─────────────────────────────────────────────────

export interface TargetSelection {
  sellers: SellerTargetsController
  /** Ключи выбранных кабинетов (`sellerTargetKey`). */
  selected: string[]
  selectedSellers: SellerTarget[]
  toggle: (key: string, checked: boolean) => void
  /** Загружает кабинеты при первом обращении; по умолчанию выбраны все активные WB/OZON/ЯМ/сайт. */
  ensureLoaded: () => void
  reset: () => void
}

export function useTargetSelection(): TargetSelection {
  const sellers = useSellerTargets()
  const { targets, loadState, load, reset: resetSellers } = sellers
  const [selected, setSelected] = useState<string[]>([])
  const defaulted = useRef(false)

  useEffect(() => {
    if (loadState === 'ready' && !defaulted.current) {
      defaulted.current = true
      setSelected(targets.map(sellerTargetKey))
    }
  }, [loadState, targets])

  const toggle = useCallback((key: string, checked: boolean) => {
    setSelected((current) => (checked ? (current.includes(key) ? current : [...current, key]) : current.filter((value) => value !== key)))
  }, [])

  const ensureLoaded = useCallback(() => {
    if (loadState === 'idle') void load()
  }, [loadState, load])

  const reset = useCallback(() => {
    defaulted.current = false
    setSelected([])
    resetSellers()
  }, [resetSellers])

  const selectedSellers = useMemo(
    () => targets.filter((target) => selected.includes(sellerTargetKey(target))),
    [targets, selected],
  )
  return { sellers, selected, selectedSellers, toggle, ensureLoaded, reset }
}

// ── Название и категория артикулов ──────────────────────────────────

/** Название и категория артикулов: пряники — из каталога пряников, макеты (`profile: 'layout'`) — из каталога товаров. */
export function useArticleMeta(
  articles: string[],
  enabled: boolean,
  profile: VisualsProfile = DEFAULT_VISUALS_PROFILE,
): { meta: Map<string, ArticleMeta>; loading: boolean } {
  const key = articles.join('|')
  const [meta, setMeta] = useState<Map<string, ArticleMeta>>(new Map())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || articles.length === 0) return
    const controller = new AbortController()
    setLoading(true)
    void (async () => {
      const next = new Map<string, ArticleMeta>()
      try {
        for (const part of chunk(articles, MAX_ARTICLES_PER_CALL)) {
          for (const item of await gingerbreadVisualsFlowAPI.loadArticleMeta(part, controller.signal, profile)) next.set(item.article, item)
        }
      } catch {
        // Без категории план покажет «состав определится при запуске»: запуск от этого не страдает.
      }
      if (!controller.signal.aborted) {
        setMeta(next)
        setLoading(false)
      }
    })()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, profile])

  return { meta, loading }
}

// ── План наборов и смета ────────────────────────────────────────────

export interface VisualsPlanState {
  plan: ArticlePlan[]
  state: 'idle' | 'loading' | 'ready' | 'error'
  error: string
  estimate: EstimateTotal | null
}

export function useVisualsPlan(
  articles: string[],
  meta: Map<string, ArticleMeta>,
  metaLoading: boolean,
  sellers: SellerTarget[],
  enabled: boolean,
  profile: VisualsProfile = DEFAULT_VISUALS_PROFILE,
): VisualsPlanState {
  const [matches, setMatches] = useState<Map<number | string, SlideSetMatch>>(new Map())
  const [state, setState] = useState<VisualsPlanState['state']>('idle')
  const [error, setError] = useState('')
  const [estimates, setEstimates] = useState<Map<string, SeriesEstimate>>(new Map())

  const skuTargets = useMemo(() => sellers.map(skuTargetOf).filter((target): target is NonNullable<typeof target> => target !== null), [sellers])
  // Запрос подбора — на каждую пару «категория × тип макета»: наборы вроде «Макеты · Круг» привязаны к типу.
  // У пряников типа нет, там пара сводится к категории, как раньше.
  const matchPairs = useMemo(() => {
    const pairs = new Map<string, { categoryId: number; designType: string | null }>()
    for (const article of articles) {
      const info = meta.get(article)
      if (info?.categoryId == null) continue
      const designType = profile === 'layout' ? info.designType || null : null
      pairs.set(String(matchKeyOf(info.categoryId, designType)), { categoryId: info.categoryId, designType })
    }
    return [...pairs.values()].sort((a, b) => a.categoryId - b.categoryId || String(a.designType).localeCompare(String(b.designType)))
  }, [articles, meta, profile])
  const categoryIds = useMemo(() => [...new Set(matchPairs.map((pair) => pair.categoryId))], [matchPairs])
  const matchKey = `${profile}#${matchPairs.map((pair) => `${pair.categoryId}|${pair.designType ?? ''}`).join(',')}#${skuTargets.map((target) => `${target.marketplace}:${target.cabinet}`).join(',')}`

  useEffect(() => {
    if (!enabled || metaLoading || skuTargets.length === 0 || categoryIds.length === 0) {
      setMatches(new Map())
      setState('idle')
      setError('')
      return
    }
    const controller = new AbortController()
    setState('loading')
    setError('')
    void (async () => {
      try {
        const entries = await Promise.all(
          matchPairs.map(async ({ categoryId, designType }) => {
            const match = designType
              ? await gingerbreadVisualsFlowAPI.matchSlideSets(categoryId, skuTargets, controller.signal, profile, designType)
              : await gingerbreadVisualsFlowAPI.matchSlideSets(categoryId, skuTargets, controller.signal, profile)
            return [matchKeyOf(categoryId, designType), match] as const
          }),
        )
        if (controller.signal.aborted) return
        setMatches(new Map(entries))
        setState('ready')
      } catch (cause) {
        if (controller.signal.aborted) return
        setMatches(new Map())
        setState('error')
        setError(errorText(cause, 'Не удалось подобрать наборы слайдов'))
      }
    })()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey, enabled, metaLoading])

  const plan = useMemo(
    () => (state === 'ready' ? buildVisualsPlan(articles, meta, matches, sellers) : []),
    [state, articles, meta, matches, sellers],
  )

  const groupsKey = distinctSetGroups(plan).map((ids) => ids.join(',')).join('|')
  useEffect(() => {
    const groups = distinctSetGroups(plan)
    if (groups.length === 0) return
    const controller = new AbortController()
    void (async () => {
      const next = new Map<string, SeriesEstimate>()
      await Promise.all(
        groups.map(async (ids) => {
          try {
            next.set(ids.join(','), await gingerbreadVisualsFlowAPI.estimate(ids, controller.signal))
          } catch {
            // Смета — справка: без неё запуск возможен, цена остаётся «уточняется».
          }
        }),
      )
      if (!controller.signal.aborted) setEstimates(next)
    })()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey])

  const estimate = useMemo(() => (plan.length > 0 ? estimateTotal(plan, estimates) : null), [plan, estimates])
  return { plan, state, error, estimate }
}

// ── Запуски ─────────────────────────────────────────────────────────

export interface SkuRunsState {
  runs: RunSummary[]
  loading: boolean
  error: string
  refresh: () => Promise<void>
}

/** Запрос запусков профиля: для пряников вызов прежний (профиль по умолчанию внутри API), для макетов профиль передаётся явно. */
function listRunsOfProfile(article: string | undefined, profile: VisualsProfile) {
  if (profile !== DEFAULT_VISUALS_PROFILE) return gingerbreadVisualsFlowAPI.listSkuRuns(article, undefined, profile)
  return article ? gingerbreadVisualsFlowAPI.listSkuRuns(article) : gingerbreadVisualsFlowAPI.listSkuRuns()
}

/**
 * SKU-запуски профиля (по умолчанию пряники) по артикулам. Один артикул — запрос с `article=`; несколько — общий
 * список (до 200 последних запусков) с отбором на клиенте. Пока есть генерирующиеся, перечитывается раз в 5 секунд.
 */
export function useSkuRuns(articles: string[], enabled: boolean, profile: VisualsProfile = DEFAULT_VISUALS_PROFILE): SkuRunsState {
  const key = articles.join('|')
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const articlesRef = useRef(articles)
  articlesRef.current = articles

  const fetchRuns = useCallback(async (silent: boolean) => {
    const list = articlesRef.current
    if (list.length === 0) {
      setRuns([])
      return
    }
    if (!silent) setLoading(true)
    try {
      const rows = await listRunsOfProfile(list.length === 1 ? list[0] : undefined, profile)
      setRuns(pickRuns(rows, list))
      setError('')
    } catch (cause) {
      if (!silent) setError(errorText(cause, 'Не удалось загрузить запуски визуалов'))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [profile])

  useEffect(() => {
    if (!enabled) return
    void fetchRuns(false)
  }, [enabled, key, fetchRuns])

  const pending = hasPendingRuns(runs)
  useEffect(() => {
    if (!enabled || !pending) return
    const timer = window.setInterval(() => void fetchRuns(true), RUNS_POLL_MS)
    return () => window.clearInterval(timer)
  }, [enabled, pending, fetchRuns])

  const refresh = useCallback(() => fetchRuns(true), [fetchRuns])
  return { runs, loading, error, refresh }
}
